import React, { useState, useRef, useEffect } from "react";
import { PlayArrow, Pause } from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic";

// ✅ Global reference to track currently playing audio
let currentlyPlayingAudio = null;

export default function VoiceMessagePlayer({ audioUrl, isMine, duration }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);

  const BASE_API_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  const fullUrl = audioUrl.startsWith("http")
    ? audioUrl
    : `${BASE_API_URL}${audioUrl}`;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Clear global reference when audio ends
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      
      // Clean up if this component unmounts while playing
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Pause current audio
      audio.pause();
      setIsPlaying(false);
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    } else {
      // ✅ Stop any currently playing audio (WhatsApp behavior)
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audio) {
        currentlyPlayingAudio.pause();
        currentlyPlayingAudio.currentTime = 0;
      }

      // Play this audio
      audio.play();
      setIsPlaying(true);
      currentlyPlayingAudio = audio;
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * audioDuration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div
      className={`flex items-center gap-2 min-w-[200px] max-w-[280px] p-2 rounded-lg ${
        isMine ? "bg-white/10" : "bg-gray-100"
      }`}
    >
      <audio ref={audioRef} src={fullUrl} preload="metadata" />

      <button
        onClick={togglePlayPause}
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
          isMine
            ? "bg-white/20 hover:bg-white/30 text-white"
            : "bg-blue-100 hover:bg-blue-200 text-blue-600"
        }`}
      >
        {isPlaying ? (
          <Pause sx={{ fontSize: 18 }} />
        ) : (
          <PlayArrow sx={{ fontSize: 18 }} />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div
          className="relative h-1 bg-white/20 rounded-full cursor-pointer group"
          onClick={handleSeek}
        >
          <div
            className={`absolute h-full rounded-full transition-all ${
              isMine ? "bg-white" : "bg-blue-500"
            }`}
            style={{ width: `${progress}%` }}
          />
          <div
            className={`absolute w-3 h-3 rounded-full transition-all transform -translate-y-1/2 top-1/2 opacity-0 group-hover:opacity-100 ${
              isMine ? "bg-white" : "bg-blue-600"
            }`}
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        <div
          className={`text-[10px] mt-1 ${
            isMine ? "text-white/70" : "text-gray-500"
          }`}
        >
          {formatTime(currentTime)} / {formatTime(audioDuration)}
        </div>
      </div>

      <div className="flex-shrink-0 text-xl">
        <MicIcon />
      </div>
    </div>
  );
}